import { useState, useCallback, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@chakra-ui/popover";
import { Portal } from "@chakra-ui/portal";
import { useDisclosure } from "@chakra-ui/hooks";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "assets/css/MiniCalendar.css";
import "./DateTimePicker.css";
import { HiOutlineCalendar } from "react-icons/hi";

type DateTimePickerProps = {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  popoverClassName?: string;
  showTime?: boolean;
  readOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  error?: boolean;
};

const formatDisplayDate = (date: Date, showTime: boolean): string => {
  if (showTime) {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

function formatTimeForInput(date: Date): string {
  const h = date.getHours().toString().padStart(2, "0");
  const m = date.getMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}

function parseTimeToDate(timeStr: string, baseDate: Date): Date {
  const [hours = 0, minutes = 0] = timeStr.split(":").map(Number);
  const d = new Date(baseDate);
  d.setHours(isNaN(hours) ? 0 : hours, isNaN(minutes) ? 0 : minutes, 0, 0);
  return d;
}

/** react-calendar rejects dates outside 1900–2100 */
function isValidCalendarDate(d: Date | null | undefined): d is Date {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return false;
  const y = d.getFullYear();
  return y >= 1900 && y <= 2100;
}

export default function DateTimePicker({
  value = null,
  onChange,
  placeholder,
  className = "",
  buttonClassName = "",
  popoverClassName = "",
  showTime = false,
  readOnly = false,
  minDate,
  maxDate,
  error = false,
}: DateTimePickerProps) {
  const defaultPlaceholder = showTime ? "Select date & time" : "Select date";
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const [internalDate, setInternalDate] = useState<Date | null>(value ?? null);
  const [timeStr, setTimeStr] = useState<string>(() =>
    value ? formatTimeForInput(value) : "09:00"
  );
  useEffect(() => {
    if (value && isValidCalendarDate(value)) {
      setInternalDate(value);
      setTimeStr(formatTimeForInput(value));
    } else {
      setInternalDate(null);
      setTimeStr("09:00");
    }
  }, [value]);

  const rawDate = value ?? internalDate;
  const currentDate = isValidCalendarDate(rawDate) ? rawDate : null;
  const displayValue = currentDate ? formatDisplayDate(currentDate, showTime) : "";

  const handleDateChange = useCallback(
    (val: Date | Date[] | null) => {
      const newDate =
        val instanceof Date && isValidCalendarDate(val)
          ? val
          : Array.isArray(val) && val[0] && isValidCalendarDate(val[0])
            ? val[0]
            : new Date();
      if (!showTime) {
        const dateOnly = new Date(newDate);
        dateOnly.setHours(0, 0, 0, 0);
        if (isValidCalendarDate(dateOnly)) {
          setInternalDate(dateOnly);
          onChange?.(dateOnly);
          onClose();
        }
        return;
      }
      const merged = parseTimeToDate(timeStr, newDate);
      if (isValidCalendarDate(merged)) {
        setInternalDate(merged);
        onChange?.(merged);
      }
    },
    [timeStr, onChange, showTime, onClose]
  );

  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const t = e.target.value;
      setTimeStr(t);
      const base = currentDate ?? new Date();
      const merged = parseTimeToDate(t, base);
      setInternalDate(merged);
      onChange?.(merged);
    },
    [currentDate, onChange]
  );

  const handleSelect = useCallback(() => {
    const base = currentDate ?? new Date();
    const merged = parseTimeToDate(timeStr, base);
    setInternalDate(merged);
    onChange?.(merged);
    onClose();
  }, [currentDate, timeStr, onChange, onClose]);

  const triggerContent = (
    <div className="relative">
      <button
        type="button"
        onClick={readOnly ? undefined : onToggle}
        disabled={readOnly}
        className={`w-full px-2.5 py-2 text-xs border ${error ? "border-red-500" : "border-[#EEE]"} rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D] pr-10 text-left ${
          !displayValue ? "text-[#6D6D6D]" : "text-[#222]"
        } ${readOnly ? "cursor-default" : ""} ${buttonClassName}`}
      >
        {displayValue || (placeholder ?? defaultPlaceholder)}
      </button>
      <HiOutlineCalendar className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6D6D6D] pointer-events-none" strokeWidth={1.5} />
    </div>
  );

  if (readOnly) {
    return triggerContent;
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
    >
      <PopoverTrigger>{triggerContent}</PopoverTrigger>
      <Portal>
        <PopoverContent className={`w-auto !rounded-md !bg-white !p-0 !shadow-xl !border !border-[#EEE] ${popoverClassName}`.trim()}>
          <div className="p-2 space-y-2">
            <div className="date-time-picker-calendar">
              <Calendar
                onChange={handleDateChange}
                value={currentDate ?? undefined}
                minDate={minDate ?? new Date(1900, 0, 1)}
                maxDate={maxDate ?? new Date(2100, 11, 31)}
                defaultView="month"
                className="border-0 !bg-transparent"
              />
            </div>
            {showTime && (
              <div className="flex items-center gap-2 pt-2 border-t border-[#EEE]">
                <label className="text-xs font-normal text-[#222]">Time</label>
                <input
                  type="time"
                  value={timeStr}
                  onChange={handleTimeChange}
                  className="flex-1 px-2.5 py-1.5 text-xs border border-[#EEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                />
                <button
                  type="button"
                  onClick={handleSelect}
                  className="px-3 py-1.5 text-xs font-semibold text-white rounded-md bg-[#222] hover:bg-[#333]"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
